import { Paper } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import { styled } from '@mui/material/styles'
import CustomSelect from '../../../components/Entry/CustomSelect'
import { DashboardPercent } from '../utils/DashboardPercent'
import { DashboardInput } from '../utils/DashboardInput'
import logo1 from '../../../assets/image/dashboard1.png'
import logo2 from '../../../assets/image/dashboard2.png'
import ProvinceService from '../../provinces/services/Provinces'
import CustomerOrderService from '../../customer_order/services/CustomerOrder'
import PlotService from '../../plot/services/Plots'
import AgroDashboardService from '../service/AgroDashboard'
import SpeciesObjectiveChart from './SpeciesObjectiveCard'
import { DashboardNumber } from '../utils/DashboardNumber'
import PlantationYearlyCumulativeChart from '../utils/PlantationCUmul'
import DataSetsService from '../../datasets/services/DataSets'

const useStyles = makeStyles((theme) => ({
    pageContent: {
        margin: 10,
        backgroundColor: '#F2F8F6',
        '& .MuiPaper-root': {
            border: 'none',
            boxShadow: 'none'
        }
    }
}))

const TablePaper = styled(Paper)(({ theme }) => ({
    boxShadow: '0px 5px 5px -3px #d2d2d2,0px 8px 10px 1px #d2d2d2,0px 3px 14px 2px #d2d2d2'
}))

const DEFAULT_VALUES = {
    customer: '',
    plot: '',
    region: '',
    fokontany: '',
    project_type: '',
    year: new Date().getFullYear()
}

export const PLantingDashBord = () => {
    const classes = useStyles()

    const [dashboardData, setDashboardData] = useState({ total_tree_planted: 0, total_surface: 0 })
    const [customers, setCustomers] = useState([])
    const [regions, setRegions] = useState([])
    const [fokontanyList, setFokontanyList] = useState([])
    const [plots, setPlots] = useState([])
    const [nurseryCount, setNurseryCount] = useState({ total: 0 })
    const [yearlyCumulativeData, setYearlyCumulativeData] = useState([])
    const [projectTypes, setProjectTypes] = useState([]) // <-- options du select "Type de projet"

    const [values, setValues] = useState({ ...DEFAULT_VALUES })

    useEffect(() => {
        DataSetsService.getDataCode('project_type')
            .then((res) => {
                const raw = res?.data?.[0]?.database

                if (!raw) {
                    setProjectTypes([])
                    return
                }

                try {
                    const parsed = JSON.parse(raw)
                    setProjectTypes(Array.isArray(parsed) ? parsed : [])
                } catch (e) {
                    console.error('Erreur parsing project_type database:', e)
                    setProjectTypes([])
                }
            })
            .catch((err) => console.error('Erreur chargement project_type:', err))
    }, [])

    useEffect(() => {
        CustomerOrderService.getAllCustomer()
            .then((users) => {
                if (users) {
                    setCustomers(users?.data || [])
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération des utilisateurs :", error)
            })
    }, [])

    useEffect(() => {
        AgroDashboardService.getPlantationYearlyCumulative()
            .then((data) => {
                if (data) {
                    setYearlyCumulativeData(data || [])
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération du cumul annuel :", error)
            })
    }, [])

    useEffect(() => {
        AgroDashboardService.getNurseryCount(values.region || undefined)
            .then((data) => {
                if (data) {
                    setNurseryCount(data || { total: 0 })
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération du nombre de pépinières :", error)
            })
    }, [values.region])

    useEffect(() => {
        PlotService.getAll()
            .then((data) => {
                if (data) {
                    setPlots(data?.data || [])
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération des parcelles :", error)
            })
    }, [])

    useEffect(() => {
        ProvinceService.getRegions()
            .then((data) => {
                if (data) {
                    setRegions(data?.data)
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération des régions :", error)
            })
    }, [])

    useEffect(() => {
        if (values.region) {
            ProvinceService.getFokontanyByRegion(values.region)
                .then((data) => {
                    if (data) {
                        setFokontanyList(data?.data || [])
                    }
                })
                .catch((error) => {
                    console.error("Une erreur s'est produite lors de la récupération des fokontany :", error)
                })
        } else {
            setFokontanyList([])
        }
        setValues((prevValues) => ({ ...prevValues, fokontany: '' }))
    }, [values.region])

    useEffect(() => {
        setValues((prevValues) => ({ ...prevValues, plot: '' }))
    }, [values.fokontany])

    useEffect(() => {
        AgroDashboardService.getPlantationDashboard({
            year: values.year,
            customerId: values.customer || undefined,
            plotId: values.plot || undefined,
            projectType: values.project_type || undefined
        })
            .then((data) => {
                if (data) {
                    console.log('Dashboard data:', data)
                    setDashboardData(data || { total_tree_planted: 0, total_surface: 0 })
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération du dashboard plantation :", error)
            })
    }, [values.year, values.customer, values.plot, values.project_type])

    const getYears = () => {
        const currentYear = new Date().getFullYear()
        const options = []
        for (let i = currentYear - 4; i <= currentYear + 4; i++) {
            options.push(i)
        }
        return options
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }))
    }

    // ── handleReset ───────────────────────────────────────────────
    // Vide tous les filtres (région, fokontany, parcelle, client, type de
    // projet) ; seule l'année reste à sa valeur par défaut (année courante).
    const handleReset = () => {
        setValues({ ...DEFAULT_VALUES })
    }

    const selectedFokontanyName = fokontanyList.find((fkt) => fkt.id === values.fokontany)?.name

    const filteredPlots = selectedFokontanyName ? plots.filter((plot) => plot.fkt === selectedFokontanyName) : plots

    const formatNumber = (number) => {
        return number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }

    return (
        <Paper className={classes.pageContent} elevation={0} style={{ backgroundColor: '#F2F8F6' }}>
            <TablePaper style={{ padding: 10, marginTop: 20, borderRadius: 10 }} elevation={8}>
                <div className="w-full">
                    <div className="flex justify-between">
                        <div>
                            <p className="font-semibold text-lg">Plantations</p>
                        </div>
                        <div>
                            <select
                                className="bg-[#0FCB8F] w-24 h-8 rounded-md border-none text-white"
                                name="year"
                                id="year"
                                value={values.year}
                                onChange={handleChange}
                            >
                                {getYears().map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex  gap-[40px] mb-[15px] w-full mt-[16px]">
                        <div className="w-full">
                            <CustomSelect
                                label="Régions"
                                name="region"
                                value={values.region || ''}
                                onChange={handleChange}
                                options={regions}
                                labelKey="name"
                                valueKey="id"
                            />
                        </div>
                        <div className="w-full">
                            <CustomSelect
                                label="Fokontany"
                                name="fokontany"
                                value={values.fokontany || ''}
                                onChange={handleChange}
                                options={fokontanyList}
                                labelKey="name"
                                valueKey="id"
                                isDisabled={!values.region}
                            />
                        </div>
                        <div className="w-full">
                            <CustomSelect
                                label="Parcelles"
                                name="plot"
                                value={values?.plot || ''}
                                onChange={handleChange}
                                options={filteredPlots}
                                labelKey="code"
                                valueKey="id"
                            />
                        </div>
                        <div className="w-full">
                            <CustomSelect
                                label="Client"
                                options={customers}
                                labelKey="firstname"
                                valueKey="id"
                                name="customer"
                                value={values.customer || ''}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="w-full">
                            <CustomSelect
                                label="Type de reboisement"
                                name="project_type"
                                value={values.project_type || ''}
                                onChange={handleChange}
                                options={projectTypes}
                                labelKey="label"
                                valueKey="label"
                            />
                        </div>
                        <div className="w-full mt-[15px] ">
                            <button
                                type="button"
                                onClick={handleReset}
                                className="shrink-0 h-10 px-4 rounded-md border border-[#0FCB8F] text-[#0FCB8F] bg-white hover:bg-[#0FCB8F] hover:text-white transition-colors"
                            >
                                Réinitialiser
                            </button>
                        </div>
                    </div>

                    <div className="flex gap-[40px] ">
                        <DashboardInput title="Objectif" content={`${dashboardData.objectif} `} />
                        <DashboardInput
                            title="Nombre d'arbres plantés"
                            showImg={true}
                            textColor="text-green-500"
                            src={logo1}
                            value={formatNumber(dashboardData.total_tree_planted)}
                        />
                        <DashboardInput
                            title="Surface plantées "
                            showImg={true}
                            textColor="text-yellow-500"
                            src={logo2}
                            value={`${formatNumber(dashboardData.total_surface)} m²`}
                        />
                    </div>

                    <div className="flex justify-between gap-[20px] mt-[40px] mb-[20px] ">
                        <DashboardPercent text="Taux de survie" value={dashboardData.survival_rate} />
                        <DashboardNumber text="Nombre total de pépinières par an" value={nurseryCount.total} />
                    </div>
                    <div>
                        <div className="w-full">
                            <SpeciesObjectiveChart
                                speciesBreakdown={dashboardData.species_breakdown}
                                objectif={dashboardData.objectif}
                            />
                        </div>
                    </div>
                </div>
            </TablePaper>
            <div className="w-full mt-5">
                <PlantationYearlyCumulativeChart data={yearlyCumulativeData} />
            </div>
        </Paper>
    )
}
